import { fillKeyReturnText, toKeyReturnDate } from 'containers/Warehouse/resignation/core/KeyReturn'
import { useHistory, useLocation } from 'react-router-dom'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import { useState } from 'react'
import { POST } from 'rest/request'
import { api } from 'rest/urls'

type WarehouseAlertSource = Pick<warehouse.Warehouse,
  'objectId' | 'hasAccessByFlag' | 'inFuture' | 'dateFrom' | 'alarmState' | 'needPayment' | 'contractPdf' |
  'contractGroupId' | 'contractSigned' | 'actions' | 'isShared' | 'permissions' | 'resignation' | 'keyReturn'>

interface Options {
  withAlarm?: boolean
}

const useWarehouseAlert = ({ objectId, hasAccessByFlag, inFuture, dateFrom, alarmState, needPayment, contractPdf, contractGroupId, contractSigned, actions, isShared, permissions, resignation, keyReturn }: Partial<WarehouseAlertSource>, { withAlarm = true }: Options = {}) => {
  const { pathname } = useLocation()
  const [isLoading, setLoading] = useState(false)
  const clearAlarm = actions?.warehouse?.clear_alarm
  const isAlarmed = withAlarm && (alarmState?.fire_alarmed === 'yes' || alarmState?.alarmed === 'yes')
  const warning = Boolean(needPayment || isAlarmed || contractSigned === false || hasAccessByFlag === false)

  const { push } = useHistory()
  const { t, lang } = useTranslation('warehouse')
  const { openPopup } = usePopupState<{ type: 'signContract', props: { contractPdf: string, contractGroupId: string } }>()
  const keyReturnReminder = getKeyReturnReminder(keyReturn, resignation)

  const alert: warehouse.WarehouseAlert | undefined = (hasAccessByFlag === false && needPayment)
    ? {
      title: t('collectionBlockTitle'),
      desc: t('collectionBlockDueInvoicesDescription'),
      buttonText: t('collectionBlockPayButton'),
      action: !pathname.startsWith(`/warehouses/${objectId}/invoices`) && (!isShared || permissions?.includes('payment'))
        ? () => {
          push(`/warehouses/${objectId}/invoices?selectAll=1`)
        }
        : undefined
    }
    : hasAccessByFlag === false
      ? {
        title: t('collectionBlockTitle'),
        desc: t('collectionBlockOtherReasonDescription'),
        buttonText: t('collectionBlockContactButton'),
        action: () => {
          push(`/help?warehouseId=${objectId}`)
        }
      }
      : contractSigned === false
        ? {
          title: t('signAgreementTitle'),
          desc: t('signAgreementDescription'),
          buttonText: t('signAgreementTitle'),
          action: contractPdf && contractGroupId
            ? () => {
              openPopup({ type: 'signContract', props: { contractPdf, contractGroupId } })
            }
            : undefined
        } : needPayment
          ? {
            title: t('paymentMissingTitle'),
            desc: t('paymentMissingDescription'),
            buttonText: t('pay2'),
            action: !pathname.startsWith(`/warehouses/${objectId}/invoices`) && (!isShared || permissions?.includes('payment'))
              ? () => {
                push(`/warehouses/${objectId}/invoices?selectAll=1`)
              }
              : undefined
          }
          : inFuture
            ? {
              title: t('comingSoon'),
              desc: `${t('youWillBeAbleToComeIn')}: ${dateFrom}`
            }
            : withAlarm && alarmState?.fire_alarmed === 'yes'
              ? {
                title: t('fireDetectedTitle'),
                desc: t('fireDetectedDescription')
              }
              : withAlarm && alarmState?.alarmed === 'yes'
                ? {
                  title: t('alarmEventTitle'),
                  desc: t('alarmEventDescription'),
                  buttonText: clearAlarm?.text,
                  action: clearAlarm
                    ? async () => {
                      if (isLoading || !clearAlarm) return
                      try {
                        if (clearAlarm.type === 'request' && clearAlarm.method === 'POST') {
                          setLoading(true)
                          const body = clearAlarm.payload
                          const response = await POST(`${api}${clearAlarm.url}`, { body })
                          if (response?.result === 'ok') {
                            setLoading(false)
                          } else {
                            throw new Error('Wrong response')
                          }

                        } else {
                          throw new Error('No supported action')
                        }
                      } catch (error) {
                        setLoading(false)
                        console.error(error)
                      }
                    }
                    : undefined
                }
                : (resignation && resignation.needPhoto && resignation.documents.length === 0)
                  ? {
                    title: t('addPhotoTitle'),
                    desc: t('addPhotoDescription'),
                    buttonText: t('addPhotoButton'),
                    action: window.location.pathname !== `/warehouses/${objectId}/termination`
                      ? () => {
                        push(`/warehouses/${objectId}/termination`)
                      } : undefined
                  }
                  : keyReturnReminder
                    ? {
                      title: t('keyReturn.reminderTitle'),
                      desc: keyReturnReminder.method === 'key_cabinet'
                        ? fillKeyReturnText(t('keyReturn.reminderCabinet'), {
                          cabinet: keyReturnReminder.cabinet?.name || '',
                          until: toKeyReturnDate(keyReturnReminder.window?.until, t, lang)
                        })
                        : fillKeyReturnText(t('keyReturn.reminderDropBox'), {
                          until: toKeyReturnDate(keyReturnReminder.window?.until, t, lang)
                        }),
                      buttonText: t('keyReturn.reminderButton'),
                      action: () => {
                        openPopup({ type: 'keyReturn', props: { warehouseId: objectId } })
                      }
                    }
                    : undefined

  return { alert, warning }
}

/** ile dni przed terminem zwrotu klucza pokazujemy przypomnienie */
const KEY_RETURN_REMINDER_DAYS = 7

/**
 * Przypomnienie o zwrocie klucza przy zblizajacym sie koncu najmu.
 * keyReturn przychodzi z rentedreserved tylko dla umowy z data konca.
 */
const getKeyReturnReminder = (keyReturn?: warehouse.KeyReturn, resignation?: warehouse.WarehouseResignation) => {
  if (!keyReturn?.required || resignation?.isCompleted) return undefined
  const now = Date.now()
  return keyReturn.items?.find(({ window: returnWindow }) => {
    const until = returnWindow?.until ? new Date(returnWindow.until).getTime() : NaN
    return !isNaN(until) && until > now && until - now <= KEY_RETURN_REMINDER_DAYS * 24 * 60 * 60 * 1000
  })
}

export default useWarehouseAlert
